import {
  useAppDispatch,
  useAppSelector,
  useBaseLayoutStepCompletion
} from 'app/hooks';
import LevelComponent from 'components/structure-page/2d/level';
import { LevelType } from 'data/types';
import { deleteStructureItem } from 'features/structure';
import { setActiveLevelDetailIndex } from 'features/ui';
import { useEffect } from 'react';
import { levelHasRequiredLevelDetailsAttributes } from 'utils/structure';
import { getLevelDetailLevels } from 'utils/structure-level-pager';
import { structureEditResetsDesigns } from 'utils/structure-edit-reset';
import { confirmationModal } from 'utils/ui';
import LevelAccordionItem from './level-accordion-item';

export default function LevelDetailsAccordion() {
  const dispatch = useAppDispatch();
  const { structure } = useAppSelector((state) => state.structure);
  const { configurations } = useAppSelector((state) => state.configurations);
  const { selectedSystems } = useAppSelector((state) => state.systems);
  const { activeLevelDetailIndex } = useAppSelector((state) => state.ui);
  const { onLevelRemoved } = useBaseLayoutStepCompletion();

  const levels = getLevelDetailLevels(structure);
  const lastIndex = Math.max(levels.length - 1, 0);
  const activeIndex = Math.min(Math.max(activeLevelDetailIndex, 0), lastIndex);

  useEffect(() => {
    if (activeLevelDetailIndex !== activeIndex) {
      dispatch(setActiveLevelDetailIndex(activeIndex));
    }
  }, [activeLevelDetailIndex, activeIndex, dispatch]);

  const handleDeleteLevel = (level: LevelType) => {
    const deleteLevel = () => {
      dispatch(deleteStructureItem({ uniqueId: level.uniqueId }));
      onLevelRemoved(level.uniqueId);
    };
    const confirmText = structureEditResetsDesigns(
      configurations,
      selectedSystems
    )
      ? `Are you sure you want to delete ${level.label}? This will reset all configurations and selected systems!`
      : `Are you sure you want to delete ${level.label}?`;
    confirmationModal(confirmText, deleteLevel);
  };

  if (levels.length === 0) {
    return (
      <div className='flex h-full w-full items-center justify-center text-center text-gray-500'>
        No levels to show yet. Set the number of levels on the Basic Info step.
      </div>
    );
  }

  return (
    <div className='mx-auto flex min-h-full w-full max-w-3xl flex-col justify-center gap-3 p-4'>
      {levels.map((level, index) => (
        <div
          key={level.uniqueId}
          id={index === activeIndex ? 'level-details-active-item' : undefined}
        >
          <LevelAccordionItem
            label={level.label}
            expanded={index === activeIndex}
            indicatorChecked={levelHasRequiredLevelDetailsAttributes(level)}
            indicatorRequired
            deleteDisabled={levels.length === 1}
            onToggle={() => dispatch(setActiveLevelDetailIndex(index))}
            onDelete={() => handleDeleteLevel(level)}
          >
            <LevelComponent level={level} showAttributes embedded />
          </LevelAccordionItem>
        </div>
      ))}
    </div>
  );
}
